import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { secureFetch } from '@/utils/SecureFetch'
import { conceptKeys } from '@/api/queries/ConceptQueries'
import type { Concept } from '@/types/Concept.types'
import type { ConceptCategory } from '@/db/Types'

async function fetchPlaybookConcepts(playbookId: string): Promise<Concept[]> {
	const response = await secureFetch(`/api/playbooks/${playbookId}/concepts`)
	if (!response.ok) {
		if (response.status === 401) throw new Error('UNAUTHORIZED')
		throw new Error(`HTTP ${response.status}`)
	}
	const data = await response.json()
	return data.concepts ?? []
}

export interface UseConceptsByCategoryReturn {
	concepts: Concept[]
	isLoading: boolean
	error: Error | null
}

export function useConceptsByCategory(
	playbookId: string,
	category: ConceptCategory,
): UseConceptsByCategoryReturn {
	const {
		data: allConcepts = [],
		isLoading,
		error,
	} = useQuery({
		queryKey: conceptKeys.baseConcepts(playbookId),
		queryFn: () => fetchPlaybookConcepts(playbookId),
		enabled: !!playbookId,
	})

	const concepts = useMemo(
		() => allConcepts.filter(c => c.category === category),
		[allConcepts, category],
	)

	return { concepts, isLoading, error }
}
